<template>
  <v-row>
    <v-col cols="12">
      <v-card class="prodv-card" flat>
        <!-- ================= HEADER BAND ================= -->
        <div class="prodv-header pl-base-bg">
          <div class="prodv-header-left">
            <div class="prodv-header-icon">
              <v-icon color="#fff" size="22">mdi-package-variant</v-icon>
            </div>
            <div>
              <div class="prodv-title">
                {{ product?.productName || "Product" }}
              </div>
              <div class="prodv-subtitle">
                Created on {{ formatDate(product?.createdAt) }}
              </div>
            </div>
          </div>

          <v-btn v-if="!isViewOnly" small class="prodv-edit-btn" @click="goToEdit">
            <v-icon left small>mdi-pencil-outline</v-icon>
            Edit
          </v-btn>
        </div>

        <div class="pa-6">
          <!-- ================= DETAILS ================= -->
          <v-row>
            <!-- LEFT -->
            <v-col cols="12" md="6">
              <div class="prodv-detail-card">
                <div class="prodv-detail-label">HSN Code</div>
                <div class="prodv-detail-value">
                  {{ product?.hsn || "-" }}
                </div>

                <div class="prodv-detail-label mt-4">Rate</div>
                <div class="prodv-detail-value prodv-detail-value--lg">
                  ₹ {{ formatCurrency(product?.rate) }}
                </div>

                <template v-if="product?.costPrice != null">
                  <div class="prodv-detail-label mt-4">Cost Price</div>
                  <div class="prodv-detail-value">
                    ₹ {{ formatCurrency(product?.costPrice) }}
                  </div>
                </template>
              </div>
            </v-col>

            <!-- RIGHT: STOCK -->
            <v-col cols="12" md="6">
              <div
                class="prodv-stock-card"
                :class="
                  isLowStock ? 'prodv-stock-card--low' : 'prodv-stock-card--ok'
                "
              >
                <template v-if="product?.trackStock">
                  <div class="prodv-stock-label">Current Stock</div>
                  <div class="prodv-stock-value">
                    {{ product?.stockQuantity }}
                    <v-icon v-if="isLowStock" size="18" class="ml-1"
                      >mdi-alert</v-icon
                    >
                  </div>

                  <v-divider class="my-3 prodv-stock-divider" />

                  <div class="prodv-stock-label">Low Stock Threshold</div>
                  <div class="prodv-threshold-value">
                    {{ product?.lowStockThreshold }}
                  </div>
                </template>

                <template v-else>
                  <div class="prodv-stock-label">Stock Tracking</div>
                  <div class="prodv-threshold-value">
                    Not tracked for this product
                  </div>
                </template>
              </div>
            </v-col>
          </v-row>

          <!-- ================= STOCK HISTORY ================= -->
          <v-row class="mt-2" v-if="product?.trackStock">
            <v-col cols="12">
              <div class="prodv-section-label">Stock History</div>

              <v-card class="prodv-history-card" outlined flat>
                <div v-if="loadingMovements" class="prodv-empty">
                  <span>Loading...</span>
                </div>

                <div v-else-if="!movements.length" class="prodv-empty">
                  <v-icon size="32" color="#c8d1db">mdi-history</v-icon>
                  <span>No stock movements yet</span>
                </div>

                <div v-else class="prodv-movement-list">
                  <div
                    v-for="m in movements"
                    :key="m._id"
                    class="prodv-movement-row"
                  >
                    <div class="prodv-movement-main">
                      <v-chip
                        x-small
                        :color="typeColor(m.type)"
                        dark
                        class="text-uppercase font-weight-bold"
                      >
                        {{ typeLabel(m.type) }}
                      </v-chip>
                      <span class="prodv-movement-by">
                        {{ m.createdBy?.name || "-" }}
                      </span>
                    </div>

                    <div class="prodv-movement-side">
                      <span
                        class="prodv-movement-delta"
                        :class="
                          m.quantityDelta >= 0
                            ? 'prodv-delta--pos'
                            : 'prodv-delta--neg'
                        "
                      >
                        {{ m.quantityDelta >= 0 ? "+" : ""
                        }}{{ m.quantityDelta }}
                      </span>
                      <span class="prodv-movement-resulting">
                        → {{ m.resultingQuantity }} left
                      </span>
                    </div>

                    <div class="prodv-movement-date">
                      {{ formatDateTime(m.createdAt) }}
                    </div>

                    <v-btn
                      v-if="m.referenceType === 'Invoice' && m.referenceId"
                      icon
                      small
                      class="prodv-movement-view-btn"
                      @click="goToInvoice(m.referenceId)"
                    >
                      <v-icon size="18" color="#0a5a90"
                        >mdi-file-document-outline</v-icon
                      >
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: "ViewProduct",

  data() {
    return {
      product: null,
      movements: [],
      loadingMovements: false,
    };
  },

  computed: {
    isLowStock() {
      return (
        this.product?.trackStock &&
        this.product?.stockQuantity <= this.product?.lowStockThreshold
      );
    },
  },

  async created() {
    const id = this.$route.params.id;

    this.product = await this.$store.dispatch("products/getProduct", id);

    if (this.product?.trackStock) {
      this.loadingMovements = true;
      try {
        this.movements = await this.$store.dispatch("products/getMovements", {
          productId: id,
        });
      } finally {
        this.loadingMovements = false;
      }
    }
  },

  methods: {
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    formatDateTime(date) {
      if (!date) return "-";
      return new Date(date).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatCurrency(val) {
      if (val == null) return "0.00";
      return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val);
    },

    typeColor(type) {
      if (type === "SALE") return "red";
      if (type === "SALE_REVERSAL") return "blue";
      if (type === "RESTOCK") return "green";
      return "grey";
    },

    typeLabel(type) {
      return type.replace("_", " ");
    },

    goToEdit() {
      this.$router.push(`/products/${this.product._id}/edit`);
    },

    goToInvoice(invoiceId) {
      this.$router.push(`/invoices/${invoiceId}/view`);
    },
  },
};
</script>

<style scoped>
.prodv-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

/* ================= HEADER BAND ================= */
.prodv-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
}

.prodv-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.prodv-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.prodv-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.prodv-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.prodv-edit-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

/* ================= DETAIL CARD ================= */
.prodv-detail-card {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 18px 20px;
  height: 100%;
}

.prodv-detail-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 4px;
}

.prodv-detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.prodv-detail-value--lg {
  font-size: 19px;
  font-weight: 700;
  color: #0f172a;
}

/* ================= STOCK CARD ================= */
.prodv-stock-card {
  border-radius: 0;
  padding: 18px 20px;
  height: 100%;
  color: #fff;
}

.prodv-stock-card--ok {
  background: linear-gradient(135deg, #052f5e 0%, #0a5a90 100%);
}

.prodv-stock-card--low {
  background: linear-gradient(135deg, #7a1f1f 0%, #b23a3a 100%);
}

.prodv-stock-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.75);
}

.prodv-stock-value {
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

.prodv-stock-divider {
  border-color: rgba(255, 255, 255, 0.2) !important;
}

.prodv-threshold-value {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin-top: 4px;
}

/* ================= STOCK HISTORY ================= */
.prodv-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.prodv-history-card {
  padding: 12px;
  max-height: 500px;
  overflow-y: auto;
}

.prodv-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #94a3b8;
  padding: 24px 0;
  font-size: 13px;
}

.prodv-movement-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.prodv-movement-row {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 10px 14px;
}

.prodv-movement-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
}

.prodv-movement-by {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.prodv-movement-side {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.prodv-movement-delta {
  font-size: 14px;
  font-weight: 700;
}

.prodv-delta--pos {
  color: #1e8e3e;
}

.prodv-delta--neg {
  color: #b23a3a;
}

.prodv-movement-resulting {
  font-size: 12.5px;
  color: #64748b;
}

.prodv-movement-date {
  font-size: 12px;
  color: #94a3b8;
  flex-shrink: 0;
  white-space: nowrap;
}

.prodv-movement-view-btn {
  flex-shrink: 0;
}
</style>
